import type { GameState, PlayerIndex } from "@playpal/core";
import {
	CodeBreakerBoard,
	FleetBoard,
	FlipFourBoard,
	PitRowBoard,
	SlidingBoard,
} from "./BoardGames";
import { MosaicBoard, NumberGuessBoard, SnapBoard, WarBoard } from "./CardBoards";
import { ChoiceDuelBoard } from "./ChoiceDuelBoard";
import { Connect4Board } from "./Connect4Board";
import { SnakesLaddersBoard, TurboTrackBoard } from "./DiceBoards";
import { DotsBoxesBoard } from "./DotsBoxesBoard";
import { CheckersBoard, GoFishBoard, OldMaidBoard } from "./HandBoards";
import { MemoryBoard } from "./MemoryBoard";
import { FiveInRowBoard, NimBoard, ReflexBoard, RpsBoard } from "./MoreBoards";
import { CharadesBoard, TypingLadderBoard } from "./PartyBoards";
import { PegChaseBoard } from "./PegChaseBoard";
import { DotsRaceBoard, LastCardBoard, MinesweeperBoard } from "./PuzzleBoards";
import { QuizBoard } from "./QuizBoard";
import { LogicGridBoard, RouteBlockerBoard, SnailDashBoard, WordLadderBoard } from "./RaceBoards";
import { RummyBoard } from "./RummyBoard";
import { SuitRunBoard, TowerSwapBoard } from "./StackBoards";
import { TicTacToeBoard } from "./TicTacToeBoard";

interface Props {
	state: GameState;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
	clockOffset: number;
}

export function GameView({ state, seat, onMove, clockOffset }: Props) {
	switch (state.kind) {
		case "tictactoe":
			return <TicTacToeBoard state={state} seat={seat} onMove={onMove} />;
		case "connect4":
			return <Connect4Board state={state} seat={seat} onMove={onMove} />;
		case "memory":
			return <MemoryBoard state={state} seat={seat} onMove={onMove} />;
		case "dotsboxes":
			return <DotsBoxesBoard state={state} seat={seat} onMove={onMove} />;
		case "nim":
			return <NimBoard state={state} seat={seat} onMove={onMove} />;
		case "fiveinrow":
			return <FiveInRowBoard state={state} seat={seat} onMove={onMove} />;
		case "rps":
			return <RpsBoard state={state} seat={seat} onMove={onMove} />;
		case "reflex":
			return <ReflexBoard state={state} seat={seat} onMove={onMove} clockOffset={clockOffset} />;
		case "suitrun":
			return <SuitRunBoard state={state} seat={seat} onMove={onMove} />;
		case "towerswap":
			return <TowerSwapBoard state={state} seat={seat} onMove={onMove} />;
		case "wordladder":
			return <WordLadderBoard state={state} seat={seat} onMove={onMove} />;
		case "snaildash":
			return <SnailDashBoard state={state} seat={seat} onMove={onMove} clockOffset={clockOffset} />;
		case "routeblocker":
			return <RouteBlockerBoard state={state} seat={seat} onMove={onMove} />;
		case "logicgrid":
			return <LogicGridBoard state={state} seat={seat} onMove={onMove} />;
		case "rummy":
			return <RummyBoard state={state} seat={seat} onMove={onMove} />;
		case "pegchase":
			return <PegChaseBoard state={state} seat={seat} onMove={onMove} />;
		case "snakesladders":
			return <SnakesLaddersBoard state={state} seat={seat} onMove={onMove} />;
		case "turbotrack":
			return <TurboTrackBoard state={state} seat={seat} onMove={onMove} />;
		case "typingladder":
			return <TypingLadderBoard state={state} seat={seat} onMove={onMove} />;
		case "charades":
			return <CharadesBoard state={state} seat={seat} onMove={onMove} />;
		case "minesweeper":
			return <MinesweeperBoard state={state} seat={seat} onMove={onMove} />;
		case "lastcard":
			return <LastCardBoard state={state} seat={seat} onMove={onMove} />;
		case "dotsrace":
			return <DotsRaceBoard state={state} seat={seat} onMove={onMove} />;
		case "checkers":
			return <CheckersBoard state={state} seat={seat} onMove={onMove} />;
		case "gofish":
			return <GoFishBoard state={state} seat={seat} onMove={onMove} />;
		case "oldmaid":
			return <OldMaidBoard state={state} seat={seat} onMove={onMove} />;
		case "fleet":
			return <FleetBoard state={state} seat={seat} onMove={onMove} />;
		case "flipfour":
			return <FlipFourBoard state={state} seat={seat} onMove={onMove} />;
		case "codebreaker":
			return <CodeBreakerBoard state={state} seat={seat} onMove={onMove} />;
		case "sliding":
			return <SlidingBoard state={state} seat={seat} onMove={onMove} />;
		case "pitrow":
			return <PitRowBoard state={state} seat={seat} onMove={onMove} />;
		case "war":
			return <WarBoard state={state} seat={seat} onMove={onMove} />;
		case "snap":
			return <SnapBoard state={state} onMove={onMove} />;
		case "numberguess":
			return <NumberGuessBoard state={state} seat={seat} onMove={onMove} />;
		case "mosaic":
			return <MosaicBoard state={state} seat={seat} onMove={onMove} />;
		case "quiz":
			return <QuizBoard state={state} seat={seat} onMove={onMove} clockOffset={clockOffset} />;
		// Everything built on the "both answer at once" framework shares a screen.
		case "times":
		case "spelling":
		case "sciencesort":
		case "geography":
		case "mathsprint":
		case "notematch":
		case "wouldyourather":
			return (
				<ChoiceDuelBoard state={state} seat={seat} onMove={onMove} clockOffset={clockOffset} />
			);
	}
}

/** The line above the board telling a player what to do next. */
export function turnHint(state: GameState, seat: PlayerIndex, friend: string): string {
	if (state.result) return "";
	// A `null` turn means both players are acting at once.
	if (state.turn === null) return "Go go go!";
	if (state.turn === seat) return "Your turn!";
	return `${friend} is thinking…`;
}
